import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { apiRequest } from "../utils/http";
import { consumeSessionExpiredNotice, isAdminRole, persistAdminSession } from "../utils/auth";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "", otp: "" });
  const [step, setStep] = useState("credentials");
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sessionAlert, setSessionAlert] = useState("");
  const cooldownTimerRef = useRef(null);

  useEffect(() => {
    setSessionAlert(consumeSessionExpiredNotice());
  }, []);

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
      if (step === "credentials" && error.includes("Too many OTP requests")) {
        setError("");
        setSessionAlert("You can request a new OTP now.");
      }
      return undefined;
    }

    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }

    cooldownTimerRef.current = setInterval(() => {
      setCooldownSeconds((current) => Math.max(0, current - 1));
    }, 1000);

    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    };
  }, [cooldownSeconds]);

  useEffect(
    () => () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    },
    []
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const formatCooldown = (seconds) => {
    const total = Math.max(0, Number(seconds) || 0);
    const minutes = String(Math.floor(total / 60)).padStart(2, "0");
    const remainingSeconds = String(total % 60).padStart(2, "0");
    return `${minutes}:${remainingSeconds}`;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    try {
      setIsSubmitting(true);
      if (step === "credentials") {
        if (cooldownSeconds > 0) {
          setError(`Too many OTP requests. Please try again in ${formatCooldown(cooldownSeconds)}`);
          return;
        }

        try {
          await apiRequest("/api/auth/login/admin/request-otp", {
            method: "POST",
            skipSessionExpiryHandling: true,
            body: JSON.stringify({ email: form.email, password: form.password })
          });
          setStep("otp");
        } catch (requestError) {
          const retryAfterSeconds = requestError?.retryAfterSeconds || 0;
          if (retryAfterSeconds > 0) {
            setCooldownSeconds(retryAfterSeconds);
            setError(`Too many OTP requests. Please try again in ${formatCooldown(retryAfterSeconds)}`);
            return;
          }

          throw requestError;
        }
        return;
      }

      const response = await apiRequest("/api/auth/login/admin/verify-otp", {
        method: "POST",
        skipSessionExpiryHandling: true,
        body: JSON.stringify({ email: form.email, otp: form.otp })
      });

      if (!isAdminRole(response?.data?.user?.role)) {
        throw new Error("Admin access required");
      }

      persistAdminSession({
        token: response?.data?.token,
        user: response?.data?.user
      });

      navigate(location.state?.from?.pathname || "/", { replace: true });
    } catch (loginError) {
      const retryAfterSeconds = loginError?.retryAfterSeconds || 0;
      if (retryAfterSeconds > 0) {
        setCooldownSeconds(retryAfterSeconds);
        setError(`Too many OTP requests. Please try again in ${formatCooldown(retryAfterSeconds)}`);
        return;
      }

      setError(loginError.message || "Unable to login");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        background:
          "radial-gradient(circle at top left, rgba(249,115,22,0.18), transparent 30%), linear-gradient(135deg, #111827 0%, #1f2937 48%, #fff7ed 160%)"
      }}
    >
      <Paper sx={{ width: "100%", maxWidth: 480, p: { xs: 3, md: 4 } }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h4" sx={{ mb: 1 }}>Admin Login</Typography>
            <Typography sx={{ color: "text.secondary" }}>
              Sign in with an admin account to manage users, listings, quotations, and storefront content.
            </Typography>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
          {sessionAlert && <Alert severity="warning">{sessionAlert}</Alert>}
          {cooldownSeconds > 0 && (
            <Alert severity="warning">
              Too many OTP requests. Please try again in {formatCooldown(cooldownSeconds)}
            </Alert>
          )}
          {cooldownSeconds === 0 && step === "credentials" && error === "Too many OTP requests. Please try again later." && (
            <Alert severity="success">You can request a new OTP now.</Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                label="Password"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                fullWidth
                disabled={step !== "credentials"}
              />
              <TextField
                label="OTP"
                name="otp"
                value={form.otp}
                onChange={handleChange}
                fullWidth
                disabled={step !== "otp"}
                helperText={step === "otp" ? "Enter the code sent to your email" : "Login first to receive a code"}
              />
              <Button type="submit" variant="contained" size="large" disabled={isSubmitting || (step === "credentials" && cooldownSeconds > 0)}>
                {isSubmitting
                  ? step === "credentials"
                    ? "Sending code..."
                    : "Verifying code..."
                  : step === "credentials"
                      ? cooldownSeconds > 0
                        ? `Wait ${formatCooldown(cooldownSeconds)}`
                        : "Send OTP"
                    : "Verify OTP"}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LoginPage;
